import React from "react";
import { Field, TextInput, TextArea, Checkbox } from "@/components/ReportPrimitives";

/**
 * Shared unit toggle used by every profile form. Imperial is the default
 * (user req #5). Switching units never converts values — it only changes
 * the label/placeholder so the user re-enters in the new system.
 */
export function UnitToggle({ value, onChange, testId = "profile-units" }) {
  return (
    <Field label="Units" helper="Imperial is the RigSentinel default. Toggle to Metric before entering metric values.">
      <div className="inline-flex rounded-md overflow-hidden" style={{ border: "1px solid var(--rs-border)" }}>
        <button
          type="button"
          data-testid={`${testId}-imp`}
          onClick={() => onChange("IMP")}
          className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider"
          style={{
            background: value === "IMP" ? "var(--rs-amber)" : "transparent",
            color: value === "IMP" ? "#0A0B0E" : "var(--rs-text-2)",
            minHeight: 32, minWidth: 56,
          }}
        >Imperial</button>
        <button
          type="button"
          data-testid={`${testId}-si`}
          onClick={() => onChange("SI")}
          className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider"
          style={{
            background: value === "SI" ? "var(--rs-amber)" : "transparent",
            color: value === "SI" ? "#0A0B0E" : "var(--rs-text-2)",
            minHeight: 32, minWidth: 56,
          }}
        >Metric</button>
      </div>
    </Field>
  );
}

/** unit label helper — Imperial primary suffixes vs SI suffixes. */
export const u = {
  depth:    (units) => (units === "SI" ? "m"      : "ft"),
  dia:      (units) => (units === "SI" ? "mm"     : "in"),
  press:    (units) => (units === "SI" ? "kPa"    : "psi"),
  density:  (units) => (units === "SI" ? "kg/m³"  : "ppg"),
  gradient: (units) => (units === "SI" ? "kPa/m"  : "psi/ft"),
  volume:   (units) => (units === "SI" ? "m³"     : "bbl"),
  cap:      (units) => (units === "SI" ? "m³/m"   : "bbl/ft"),
  disp:     (units) => (units === "SI" ? "m³/stk" : "bbl/stk"),
  spm:      ()      => "spm",   // unit-system invariant
};

/** Helper: render a labeled numeric input bound to value[field]. */
export function NumField({ value, onChange, field, label, units, kind, placeholder, helper, testId }) {
  const suffix = kind && u[kind] ? u[kind](units) : "";
  return (
    <Field
      label={suffix ? `${label} (${suffix})` : label}
      helper={helper}
      testId={testId ? `${testId}-field` : undefined}
    >
      <TextInput
        type="number"
        value={value[field] ?? ""}
        onChange={(v) => onChange({ ...value, [field]: v })}
        placeholder={placeholder}
        testId={testId}
      />
    </Field>
  );
}

/** Helper: render a labeled text input. */
export function TextField({ value, onChange, field, label, helper, placeholder, testId }) {
  return (
    <Field label={label} helper={helper} testId={testId ? `${testId}-field` : undefined}>
      <TextInput
        value={value[field] ?? ""}
        onChange={(v) => onChange({ ...value, [field]: v })}
        placeholder={placeholder}
        testId={testId}
      />
    </Field>
  );
}

/** Helper: notes textarea. */
export function NotesField({ value, onChange, testId = "profile-notes" }) {
  return (
    <Field label="Notes" helper="Optional. Free-text rig context for this profile.">
      <TextArea
        value={value.notes ?? ""}
        onChange={(v) => onChange({ ...value, notes: v })}
        rows={2}
        testId={testId}
      />
    </Field>
  );
}

export { Checkbox };
